import { Menu as MenuPrimitive } from '@base-ui/react/menu';
import { Check } from 'lucide-react';
import { cn } from '../../cn';

// Thin shadcn-style wrapper over Base UI's Menu primitive, mirroring the
// Select / Popover wrappers already in this folder. Introduced for the
// resource framework's per-row action menu (#1287); reusable for other
// dropdown menus (e.g. #1291's HeaderBar menu).
export const DropdownMenu = MenuPrimitive.Root;
export const DropdownMenuTrigger = MenuPrimitive.Trigger;

// Positioner-only props are derived from Base UI's own type — not enumerated
// by hand — so future additions to the anchor-positioning surface flow in
// automatically. `Omit<Positioner.Props, keyof Popup.Props>` strips the keys
// both share via BaseUIComponentProps. The `data-*` index signature passes
// through Omit, so callers can still set `data-*` on the Positioner.
type DropdownPositionerOnlyProps = Omit<
  MenuPrimitive.Positioner.Props,
  keyof MenuPrimitive.Popup.Props
>;

type DropdownMenuContentProps = MenuPrimitive.Popup.Props & {
  /** Forwarded to Positioner. Lets callers tune side/align/collision/etc. */
  positionerProps?: DropdownPositionerOnlyProps;
  /** Extra classes for Positioner — primarily z-index override when nested in another portal/modal. */
  positionerClassName?: string;
};

export function DropdownMenuContent({
  className,
  positionerProps,
  positionerClassName,
  // Lift portal-owner attribution to the Positioner so click-outside detectors
  // using `target.closest('[data-portal-owner]')` match the whole owned
  // subtree, not just Popup (mirrors Popover/Select).
  'data-portal-owner': dataPortalOwner = 'dropdown-menu',
  ...props
}: DropdownMenuContentProps & { 'data-portal-owner'?: string }) {
  return (
    <MenuPrimitive.Portal>
      <MenuPrimitive.Positioner
        sideOffset={4}
        align="end"
        {...positionerProps}
        className={cn('z-50', positionerClassName)}
        data-portal-owner={dataPortalOwner}
      >
        <MenuPrimitive.Popup
          data-slot="dropdown-menu-content"
          className={cn(
            // Bound the menu to the space the positioner leaves between the
            // trigger and the viewport edge (Base UI publishes it as
            // `--available-height` on the Positioner) and let it scroll, so a
            // long menu never runs off-screen with its lower items unreachable.
            'min-w-[8rem] max-h-[var(--available-height)] overflow-y-auto rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-md outline-none',
            className,
          )}
          {...props}
        />
      </MenuPrimitive.Positioner>
    </MenuPrimitive.Portal>
  );
}

type DropdownMenuItemProps = MenuPrimitive.Item.Props & {
  /** `destructive` tints the item for dangerous actions (e.g. delete). */
  variant?: 'default' | 'destructive';
};

export function DropdownMenuItem({
  className,
  variant = 'default',
  ...props
}: DropdownMenuItemProps) {
  return (
    <MenuPrimitive.Item
      data-slot="dropdown-menu-item"
      className={cn(
        'relative flex w-full cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none data-[highlighted]:bg-surface-2 data-[highlighted]:text-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        variant === 'destructive' &&
          'text-destructive data-[highlighted]:bg-destructive/10 data-[highlighted]:text-destructive',
        className,
      )}
      {...props}
    />
  );
}

// Checkbox menu item — for stateful toggles that keep the menu open on click
// (e.g. column show/hide, #1288). Base UI's CheckboxItem defaults to
// `closeOnClick={false}`, so toggling several columns in a row doesn't dismiss
// the menu. Renders a leading check indicator in the same 2-col gap as the
// plain items.
export function DropdownMenuCheckboxItem({
  className,
  children,
  ...props
}: MenuPrimitive.CheckboxItem.Props) {
  return (
    <MenuPrimitive.CheckboxItem
      data-slot="dropdown-menu-checkbox-item"
      className={cn(
        'relative flex w-full cursor-pointer select-none items-center gap-2 rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none data-[highlighted]:bg-surface-2 data-[highlighted]:text-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        className,
      )}
      {...props}
    >
      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
        <MenuPrimitive.CheckboxItemIndicator>
          <Check className="h-4 w-4" />
        </MenuPrimitive.CheckboxItemIndicator>
      </span>
      {children}
    </MenuPrimitive.CheckboxItem>
  );
}

export function DropdownMenuSeparator({ className, ...props }: MenuPrimitive.Separator.Props) {
  return (
    <MenuPrimitive.Separator
      data-slot="dropdown-menu-separator"
      className={cn('-mx-1 my-1 h-px bg-muted', className)}
      {...props}
    />
  );
}
